import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import { useAuth } from '../../contexts/AuthContext';
import { voiceActorService } from '../../services/voiceActorService';
import Icon from '../../components/AppIcon';
import TrendingPerformerCard from './components/TrendingPerformerCard';
import RecentActivityFeed from './components/RecentActivityFeed';
import FilterBar from './components/FilterBar';

const TrendingPerformersFeed = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [performers, setPerformers] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    sortBy: 'growth',
    timeframe: 'week',
    specialization: 'all'
  });

  useEffect(() => {
    loadTrendingData();
  }, [filters]);

  const loadTrendingData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all voice actors with follower data
      const allActors = await voiceActorService?.getAllVoiceActors();
      
      // Fetch follower counts and calculate metrics for each actor
      const actorsWithMetrics = await Promise.all(
        allActors?.map(async (actor) => {
          try {
            const followerCount = await voiceActorService?.getFollowerCount(actor?.id);
            const assignments = await voiceActorService?.getAssignments({ voiceActorId: actor?.id });
            const completedAssignments = assignments?.filter(a => a?.status === 'completed')?.length || 0;
            const totalAssignments = assignments?.length || 0;
            
            // Calculate engagement velocity (mock calculation based on available data)
            const engagementVelocity = Math.floor((followerCount * 0.15) + (completedAssignments * 2));
            
            // Calculate growth percentage (simulated based on follower count and activity)
            const growthPercentage = followerCount > 0 
              ? Math.floor(10 + (completedAssignments * 3) + Math.random() * 15)
              : Math.floor(5 + Math.random() * 10);

            return {
              ...actor,
              followerCount,
              growthPercentage,
              engagementVelocity,
              completedProjects: completedAssignments,
              totalProjects: totalAssignments,
              qualityScore: totalAssignments > 0 ? Math.floor(85 + Math.random() * 15) : 90,
              isRising: growthPercentage > 15 || (followerCount > 5 && completedAssignments > 2)
            };
          } catch (err) {
            console.error(`Error fetching metrics for actor ${actor?.id}:`, err);
            return {
              ...actor,
              followerCount: 0,
              growthPercentage: 0,
              engagementVelocity: 0,
              completedProjects: 0,
              totalProjects: 0,
              qualityScore: 0,
              isRising: false
            };
          }
        }) || []
      );

      // Apply filters
      let filteredPerformers = actorsWithMetrics;

      // Filter by specialization
      if (filters?.specialization !== 'all') {
        filteredPerformers = filteredPerformers?.filter(p => 
          p?.specializations?.includes(filters?.specialization)
        );
      }

      // Sort by selected criteria
      switch (filters?.sortBy) {
        case 'growth':
          filteredPerformers?.sort((a, b) => b?.growthPercentage - a?.growthPercentage);
          break;
        case 'followers':
          filteredPerformers?.sort((a, b) => b?.followerCount - a?.followerCount);
          break;
        case 'engagement':
          filteredPerformers?.sort((a, b) => b?.engagementVelocity - a?.engagementVelocity);
          break;
        case 'quality':
          filteredPerformers?.sort((a, b) => b?.qualityScore - a?.qualityScore);
          break;
        default:
          break;
      }

      setPerformers(filteredPerformers);

      // Generate recent activity feed
      const activities = generateRecentActivity(actorsWithMetrics);
      setRecentActivity(activities);

    } catch (err) {
      console.error('Error loading trending data:', err);
      setError('Failed to load trending performers');
    } finally {
      setLoading(false);
    }
  };

  const generateRecentActivity = (actors) => {
    const activities = [];
    const now = new Date();

    actors?.forEach((actor) => {
      // Simulate recent follower activity
      if (actor?.followerCount > 0) {
        const hoursAgo = Math.floor(Math.random() * 48);
        activities?.push({
          id: `follow-${actor?.id}-${hoursAgo}`,
          type: 'new_follower',
          actorId: actor?.id,
          actorName: actor?.fullName || actor?.stageName,
          actorHeadshot: actor?.headshotUrl,
          timestamp: new Date(now - hoursAgo * 60 * 60 * 1000),
          description: 'gained new followers'
        });
      }

      // Simulate project completion activity
      if (actor?.completedProjects > 0) {
        const hoursAgo = Math.floor(Math.random() * 72);
        activities?.push({
          id: `project-${actor?.id}-${hoursAgo}`,
          type: 'project_completed',
          actorId: actor?.id,
          actorName: actor?.fullName || actor?.stageName,
          actorHeadshot: actor?.headshotUrl,
          timestamp: new Date(now - hoursAgo * 60 * 60 * 1000),
          description: 'completed a new project'
        });
      }

      // Simulate profile update activity
      if (actor?.updatedAt) {
        const updateDate = new Date(actor?.updatedAt);
        const hoursSinceUpdate = Math.floor((now - updateDate) / (1000 * 60 * 60));
        if (hoursSinceUpdate < 168) { // Within last week
          activities?.push({
            id: `update-${actor?.id}`,
            type: 'profile_updated',
            actorId: actor?.id,
            actorName: actor?.fullName || actor?.stageName,
            actorHeadshot: actor?.headshotUrl,
            timestamp: updateDate,
            description: 'updated their profile'
          });
        }
      }
    });

    // Sort by most recent and limit to 20
    return activities
      ?.sort((a, b) => b?.timestamp - a?.timestamp)
      ?.slice(0, 20);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handlePerformerClick = (performerId) => {
    navigate(`/performer-profile/${performerId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent mb-4"></div>
            <p className="text-muted-foreground">Loading trending performers...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-red-400 mx-auto mb-4" />
            <p className="text-muted-foreground">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const risingPerformers = performers?.filter(p => p?.isRising)?.slice(0, 6);
  const topPerformers = performers?.slice(0, 12);

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation />
      
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-card-foreground mb-2">
            Trending <span className="gradient-text">Performers</span>
          </h1>
          <p className="text-muted-foreground">
            Discover rising voice actors, recent activity, and platform engagement metrics
          </p>
        </div>

        {/* Filter Bar */}
        <FilterBar filters={filters} onFilterChange={handleFilterChange} />

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mt-8">
          {/* Left Column - Trending & Rising Performers */}
          <div className="lg:col-span-2 space-y-8">
            {/* Rising Talent Section */}
            {risingPerformers?.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="TrendingUp" size={24} className="text-purple-500" />
                  <h2 className="text-2xl font-bold text-card-foreground">Rising Talent</h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {risingPerformers?.map((performer) => (
                    <TrendingPerformerCard
                      key={performer?.id}
                      performer={performer}
                      onClick={() => handlePerformerClick(performer?.id)}
                      isAuthenticated={!!user}
                      showRisingBadge={true}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* All Trending Performers */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Icon name="Flame" size={24} className="text-orange-500" />
                <h2 className="text-2xl font-bold text-card-foreground">Top Performers</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {topPerformers?.map((performer) => (
                  <TrendingPerformerCard
                    key={performer?.id}
                    performer={performer}
                    onClick={() => handlePerformerClick(performer?.id)}
                    isAuthenticated={!!user}
                    showRisingBadge={false}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Recent Activity Feed */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="Activity" size={24} className="text-blue-500" />
                <h2 className="text-2xl font-bold text-card-foreground">Recent Activity</h2>
              </div>
              <RecentActivityFeed 
                activities={recentActivity} 
                onActivityClick={handlePerformerClick}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrendingPerformersFeed;